/**
 * @file useScrollFade.ts
 * @description Затухание края у горизонтально прокручиваемого ряда (phase19.31,
 * шаг О4): вкладки и фильтры-пилюли на телефоне прокручиваются, а край ряда
 * гаснет там, где за ним ещё что-то есть, — так видно, что ряд можно листать.
 *
 * Возвращает ref ряда и готовый `style` с маской. Нет прокрутки — нет маски
 * (последняя пилюля не должна выглядеть обрезанной, когда всё и так влезло).
 *
 * ```tsx
 * const fade = useScrollFade<HTMLDivElement>();
 * <div ref={fade.ref} onScroll={fade.update} style={fade.style} className="overflow-x-auto">…</div>
 * ```
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";

/**
 * Ширина затухания края. 2,5rem, а не 1,5: на 360 пилюля «Отменённые»
 * обрезалась точно по краю, и узкое затухание не читалось как «листается»
 * (дефект приёмки 2026-10-02) — теперь гаснет заметная часть пилюли.
 */
export const SCROLL_FADE_WIDTH = "2.5rem";
const FADE = SCROLL_FADE_WIDTH;

/**
 * `fadeWidth` — своя ширина затухания (по умолчанию `SCROLL_FADE_WIDTH`):
 * шкале «Путь заказа» хватает узкой, иначе гасла подпись крайней стадии.
 */
export function useScrollFade<T extends HTMLElement>(fadeWidth: string = FADE) {
  const ref = useRef<T>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    // Полпикселя запаса: дробные ширины на масштабе экрана дают «почти ноль».
    const next = { start: el.scrollLeft > 0.5, end: max - el.scrollLeft > 0.5 };
    setEdges((prev) => (prev.start === next.start && prev.end === next.end ? prev : next));
  }, []);

  useEffect(() => {
    update();
    const el = ref.current;
    // Шрифты догружаются после первого замера — ширина подписей меняется,
    // а размер самого ряда нет (ряд во всю ширину), и наблюдатель молчит.
    let alive = true;
    void document.fonts?.ready.then(() => {
      if (alive) update();
    });
    // В тестовом окружении (jsdom) наблюдателя размеров нет — хватит первого замера.
    if (!el || typeof ResizeObserver === "undefined") {
      return () => {
        alive = false;
      };
    }
    const observer = new ResizeObserver(update);
    // Следим и за содержимым: переполнение растёт вместе с детьми, а не с
    // рядом — на 360 вкладки карточки заказа оставались без затухания края,
    // если подписи дорисовались после замера (приёмка qa4 п. 12).
    const watch = () => {
      observer.disconnect();
      observer.observe(el);
      for (const child of Array.from(el.children)) observer.observe(child);
    };
    watch();
    // Дети появились позже (пилюли после загрузки счётчиков, новая вкладка) —
    // наблюдаем и их, и пересчитываем края сразу.
    const mutations =
      typeof MutationObserver === "undefined"
        ? null
        : new MutationObserver(() => {
            watch();
            update();
          });
    mutations?.observe(el, { childList: true });
    return () => {
      alive = false;
      observer.disconnect();
      mutations?.disconnect();
    };
  }, [update]);

  const style: CSSProperties | undefined =
    edges.start || edges.end
      ? {
          maskImage: `linear-gradient(to right, ${edges.start ? "transparent" : "black"} 0, black ${fadeWidth}, black calc(100% - ${fadeWidth}), ${edges.end ? "transparent" : "black"} 100%)`,
        }
      : undefined;

  return { ref, update, style, edges };
}
